import { useState, useEffect } from 'react';
import { createShareLink, revokeShare, updateShareSettings, getSharedNote } from '../../services/sharing.js';
import Icon from '../common/Icon';
import './ShareModal.css';
import './Modals.css';

export default function ShareModal({ note, isOpen, onClose }) {
    const [loading, setLoading] = useState(false);
    const [shareData, setShareData] = useState(null);
    const [isPublic, setIsPublic] = useState(true);
    const [emailInput, setEmailInput] = useState('');
    const [sharedEmails, setSharedEmails] = useState([]);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState(null);

    // Reset state when modal opens or note changes
    useEffect(() => {
        if (isOpen && note) {
            setError(null);
            setCopied(false);
            if (note.shareId) {
                fetchShareDetails(note.shareId);
            } else {
                setShareData(null);
                setSharedEmails([]);
                setIsPublic(true);
            }
        }
    }, [isOpen, note]);

    const fetchShareDetails = async (shareId) => {
        setLoading(true);
        try {
            const data = await getSharedNote(shareId);
            setShareData(data);
            if (data) {
                setSharedEmails(data.sharedWithEmails || []);
                setIsPublic(data.isPublic !== false);
            }
        } catch (err) {
            console.error('Failed to fetch share details:', err);
            setError('Failed to load share details.');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateLink = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await createShareLink(note, {
                isPublic,
                sharedWithEmails: sharedEmails
            });

            setShareData({
                shareId: result.shareId,
                shareUrl: result.shareUrl,
                viewCount: 0,
                createdAt: new Date(),
                isPublic
            });
        } catch (err) {
            console.error('Failed to generate link:', err);
            setError('Failed to create share link.');
        } finally {
            setLoading(false);
        }
    };

    const handleRevokeLink = async () => {
        if (!confirm('Are you sure? The link will stop working immediately.')) return;

        setLoading(true);
        try {
            await revokeShare(shareData.shareId);
            setShareData(null);
            setSharedEmails([]);
            setIsPublic(true);
        } catch (err) {
            console.error('Failed to revoke link:', err);
            setError('Failed to revoke link.');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateSettings = async (updates) => {
        if (!shareData?.shareId) return;

        // Optimistic update
        if (updates.isPublic !== undefined) setIsPublic(updates.isPublic);
        if (updates.sharedWithEmails !== undefined) setSharedEmails(updates.sharedWithEmails);

        try {
            await updateShareSettings(shareData.shareId, updates);
        } catch (err) {
            console.error('Failed to update settings:', err);
            setError('Failed to save settings.');
        }
    };

    const handleCopyLink = () => {
        if (shareData?.shareUrl || (shareData?.shareId && `https://pagenotes.live/s/${shareData.shareId}`)) {
            const url = shareData.shareUrl || `https://pagenotes.live/s/${shareData.shareId}`;
            navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleAddEmail = () => {
        if (!emailInput || !emailInput.includes('@')) return;
        if (sharedEmails.includes(emailInput)) return;

        const newEmails = [...sharedEmails, emailInput];
        setSharedEmails(newEmails);
        setEmailInput('');

        if (shareData?.shareId) {
            handleUpdateSettings({ sharedWithEmails: newEmails });
        }
    };

    const handleRemoveEmail = (email) => {
        const newEmails = sharedEmails.filter(e => e !== email);
        setSharedEmails(newEmails);

        if (shareData?.shareId) {
            handleUpdateSettings({ sharedWithEmails: newEmails });
        }
    };

    if (!isOpen) return null;

    const shareUrl = shareData ? (shareData.shareUrl || `https://pagenotes.live/s/${shareData.shareId}`) : '';

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-container share-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Share Note</h2>
                    <button className="modal-close" onClick={onClose}><Icon name="X" size="sm" /></button>
                </div>

                <div className="modal-body">
                    {error && <div className="modal-error">{error}</div>}

                    {loading && !shareData && (
                        <div className="loading-state">
                            <div className="loading-spinner"><Icon name="Loader2" className="animate-spin" /></div>
                            <p>Loading share details...</p>
                        </div>
                    )}

                    {!loading && !shareData && (
                        <div className="share-initial-state">
                            <p className="share-description">
                                Create a public link to share this note with others.
                                Anyone with the link will be able to view it.
                            </p>

                            <div className="form-group">
                                <label className="toggle-label">
                                    <input
                                        type="checkbox"
                                        checked={isPublic}
                                        onChange={e => setIsPublic(e.target.checked)}
                                    />
                                    Make public (anyone with link can view)
                                </label>
                            </div>

                            <button className="generate-btn" onClick={handleGenerateLink}>
                                <Icon name="Link" size="sm" className="inline mr-2" /> Generate Share Link
                            </button>
                        </div>
                    )}

                    {shareData && (
                        <div className="share-active-state">
                            {/* Link Display */}
                            <div className="share-section">
                                <h3>Share Link</h3>
                                <div className="share-link-box">
                                    <input
                                        type="text"
                                        readOnly
                                        value={shareUrl}
                                        className="share-link-input"
                                    />
                                    <button className="copy-btn" onClick={handleCopyLink}>
                                        {copied ? <Icon name="Check" size="sm" /> : 'Copy'}
                                    </button>
                                </div>
                            </div>

                            {/* Access Control */}
                            <div className="share-controls">
                                <label className="toggle-label">
                                    <input
                                        type="checkbox"
                                        checked={isPublic}
                                        onChange={e => handleUpdateSettings({ isPublic: e.target.checked })}
                                    />
                                    Public access
                                </label>

                                <button className="revoke-btn" onClick={handleRevokeLink}>
                                    Stop Sharing
                                </button>
                            </div>

                            {/* Email Sharing */}
                            <div className="share-section">
                                <h3>Shared with people</h3>
                                <div className="email-input-group">
                                    <input
                                        type="email"
                                        placeholder="Add email address..."
                                        value={emailInput}
                                        onChange={e => setEmailInput(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleAddEmail()}
                                    />
                                    <button className="add-email-btn" onClick={handleAddEmail}>Add</button>
                                </div>
                                <div className="shared-emails-list">
                                    {sharedEmails.map(email => (
                                        <div key={email} className="email-chip">
                                            <span>{email}</span>
                                            <span
                                                className="remove-email"
                                                onClick={() => handleRemoveEmail(email)}
                                            ><Icon name="X" size="sm" /></span>
                                        </div>
                                    ))}
                                    {sharedEmails.length === 0 && (
                                        <p className="text-muted text-sm">No specific people added yet.</p>
                                    )}
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="share-stats">
                                <div className="stat-item">
                                    <span className="stat-label">Views</span>
                                    <span className="stat-value">{shareData.viewCount || 0}</span>
                                </div>
                                <div className="stat-item">
                                    <span className="stat-label">Created</span>
                                    <span className="stat-value">
                                        {shareData.createdAt ? new Date(shareData.createdAt.seconds * 1000 || shareData.createdAt).toLocaleDateString() : 'Just now'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
